import React, { useMemo } from 'react';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import AutoFixHighOutlinedIcon from '@mui/icons-material/AutoFixHighOutlined';
import { resolveAnyStaticUrl } from '../../../urlConfig';
import AdminGraniteTypeSwitcher from './AdminGraniteTypeSwitcher';
import {
    SURFACE_FINISH,
    SURFACE_POLICY_MODE,
    detectSurfaceFinishRole,
    getEffectiveSurfaceFinish,
    getOriginalGltfMaterialName,
    getUnpolishedSurfaceCatalog,
    getSlotSurfaceOverride,
    getSurfaceFinishLabel,
    getSurfaceFinishShortLabel,
    normalizeSurfaceFinishPolicy,
    updateSurfaceFinishSlotOverride,
} from './graniteSurfaceFinish.utils';

const getOriginalTextureUrl = (material) => {
    const image = material?.map?.image;

    return String(
        image?.currentSrc
        || image?.src
        || image?.url
        || ''
    ).trim();
};

const normalizeIndexes = (value = []) => Array.from(new Set(
    (Array.isArray(value) ? value : [])
        .map((item) => Math.max(0, Math.floor(Number(item) || 0)))
)).sort((left, right) => left - right);

const AdminMaterialVariantSelector = ({
    catalogs = [],
    variants = [],
    activeMaterialKey = '',
    defaultMaterialKey = '',
    modelMaterials = [],
    surfaceFinishPolicy = null,
    saving = false,
    onSelectVariant,
    onAddVariant,
    onSetDefaultVariant,
    onRemoveVariant,
    onChangeVariants,
    onChangeSurfaceFinishPolicy,
    priceSummaryByKey = {},
}) => {

    const safeVariants = Array.isArray(variants) ? variants : [];
    const safeCatalogs = Array.isArray(catalogs) ? catalogs : [];
    const safeModelMaterials = Array.isArray(modelMaterials) && modelMaterials.length
        ? modelMaterials
        : [null];
    const normalizedSurfacePolicy = normalizeSurfaceFinishPolicy(surfaceFinishPolicy);

    const unpolishedSurfaceCatalog = useMemo(
        () => getUnpolishedSurfaceCatalog(safeCatalogs),
        [safeCatalogs]
    );
    const hasDetectedUnpolishedSlots = useMemo(() => safeModelMaterials.some((material, index) => (
        detectSurfaceFinishRole(material, index) === SURFACE_FINISH.UNPOLISHED
    )), [safeModelMaterials]);

    const catalogByKey = useMemo(() => new Map(
        safeCatalogs.map((catalog) => [String(catalog?.key || ''), catalog])
    ), [safeCatalogs]);

    const resolvedActiveKey = activeMaterialKey
        || defaultMaterialKey
        || safeVariants[0]?.materialKey
        || 'gabbro-diabase';
    const activeVariant = safeVariants.find((variant) => variant?.materialKey === resolvedActiveKey)
        || safeVariants[0]
        || null;
    const activeCatalog = activeVariant
        ? catalogByKey.get(String(activeVariant.materialKey || ''))
        : null;
    const binding = activeVariant?.textureBinding || {
        mode: 'auto',
        applyToAll: true,
        materialIndexes: [],
    };
    const bindingMode = binding?.mode === 'manual' ? 'manual' : 'auto';
    const selectedIndexes = useMemo(() => new Set(
        bindingMode === 'auto' || binding.applyToAll !== false
            ? safeModelMaterials.map((_material, index) => index)
            : normalizeIndexes(binding.materialIndexes)
    ), [bindingMode, binding.applyToAll, binding.materialIndexes, safeModelMaterials]);

    const activeTexturePath = String(
        activeCatalog?.texturePreset?.path
        || activeCatalog?.texturePreset?.url
        || ''
    ).trim();
    const activeTextureUrl = activeTexturePath
        ? resolveAnyStaticUrl(activeTexturePath)
        : '';
    const unpolishedTexturePath = String(
        unpolishedSurfaceCatalog?.texturePreset?.path
        || unpolishedSurfaceCatalog?.texturePreset?.url
        || ''
    ).trim();
    const unpolishedTextureUrl = unpolishedTexturePath
        ? resolveAnyStaticUrl(unpolishedTexturePath)
        : '';

    const updateBinding = (nextBinding, mode = 'manual') => {
        if (!activeVariant || typeof onChangeVariants !== 'function') return;

        onChangeVariants(safeVariants.map((variant) => (
            variant?.materialKey === activeVariant.materialKey
                ? {
                    ...variant,
                    textureBinding: {
                        mode: mode === 'manual' ? 'manual' : 'auto',
                        applyToAll: nextBinding?.applyToAll !== false,
                        materialIndexes: normalizeIndexes(nextBinding?.materialIndexes),
                    },
                }
                : variant
        )));
    };

    const setSlotTextureMode = (index, mode) => {
        const next = new Set(selectedIndexes);

        if (mode === 'stone') next.add(index);
        else next.delete(index);

        const nextIndexes = Array.from(next).sort((left, right) => left - right);
        const appliesToEverySlot = nextIndexes.length === safeModelMaterials.length;

        updateBinding({
            applyToAll: appliesToEverySlot,
            materialIndexes: appliesToEverySlot ? [] : nextIndexes,
        });
    };

    const updateSurfacePolicy = (patch) => {
        if (typeof onChangeSurfaceFinishPolicy !== 'function') return;

        onChangeSurfaceFinishPolicy(normalizeSurfaceFinishPolicy({
            ...normalizedSurfacePolicy,
            ...patch,
        }));
    };

    const setSlotFinish = (index, finish) => {
        if (!normalizedSurfacePolicy.allowPolishOverride) return;

        onChangeSurfaceFinishPolicy?.(
            updateSurfaceFinishSlotOverride(
                normalizedSurfacePolicy,
                index,
                finish
            )
        );
    };



    return (
        <section className="adminMaterialVariantSelector" aria-label="Материалы изделия">
            <AdminGraniteTypeSwitcher
                mode="panel"
                catalogs={safeCatalogs}
                variants={safeVariants}
                activeMaterialKey={resolvedActiveKey}
                defaultMaterialKey={defaultMaterialKey}
                priceSummaryByKey={priceSummaryByKey}
                saving={saving}
                onSelectVariant={onSelectVariant}
                onAddVariant={onAddVariant}
                onSetDefaultVariant={onSetDefaultVariant}
                onRemoveVariant={onRemoveVariant}
            />

            <details className="adminMaterialVariantSelector__advanced">
                <summary>
                    <span>Обработка 3D-модели</span>
                    <small>Автоматически: неполированные части заблокированы, полированные получают выбранный гранит.</small>
                </summary>

                <div className="adminMaterialVariantSelector__slots">
                <div className="adminMaterialVariantSelector__slotsHead">
                    <div>
                        <strong>Части 3D-модели и обработка</strong>
                        <span>
                            Названия GLB распознаются автоматически, включая суффиксы .001, .002 и копии Blender.
                        </span>
                    </div>
                </div>

                <div className="adminMaterialVariantSelector__surfacePolicy">
                    <label className="adminMaterialVariantSelector__surfaceToggle">
                        <input
                            type="checkbox"
                            checked={normalizedSurfacePolicy.allowPolishOverride}
                            onChange={(event) => updateSurfacePolicy({
                                allowPolishOverride: event.target.checked,
                                mode: SURFACE_POLICY_MODE.MODEL,
                                slotOverrides: [],
                            })}
                            disabled={saving}
                        />
                        <span>
                            <strong>Разрешить менять полировку этого товара</strong>
                            <small>
                                Выключено по умолчанию. Неполированные части нельзя случайно сделать глянцевыми.
                            </small>
                        </span>
                    </label>

                    {normalizedSurfacePolicy.allowPolishOverride ? (
                        <div className="adminMaterialVariantSelector__surfaceQuickActions">
                            <button
                                type="button"
                                className={normalizedSurfacePolicy.mode === SURFACE_POLICY_MODE.MODEL ? 'is-active' : ''}
                                onClick={() => updateSurfacePolicy({
                                    mode: SURFACE_POLICY_MODE.MODEL,
                                    slotOverrides: [],
                                })}
                                disabled={saving}
                            >
                                Как в модели
                            </button>
                            <button
                                type="button"
                                className={normalizedSurfacePolicy.mode === SURFACE_POLICY_MODE.ALL_POLISHED ? 'is-active' : ''}
                                onClick={() => updateSurfacePolicy({
                                    mode: SURFACE_POLICY_MODE.ALL_POLISHED,
                                    slotOverrides: [],
                                })}
                                disabled={saving}
                            >
                                <AutoFixHighOutlinedIcon fontSize="small" />
                                Полировать все каменные части
                            </button>
                        </div>
                    ) : (
                        <div className="adminMaterialVariantSelector__surfaceLocked">
                            <LockOutlinedIcon fontSize="small" />
                            Обработка зафиксирована по материалам GLB
                        </div>
                    )}
                </div>

                <div className="adminMaterialVariantSelector__slotGrid">
                    {safeModelMaterials.map((material, index) => {
                        const usesStoneTexture = selectedIndexes.has(index);
                        const originalTextureUrl = getOriginalTextureUrl(material);
                        const originalName = getOriginalGltfMaterialName(material, index);
                        const detectedFinish = detectSurfaceFinishRole(material, index);
                        const explicitFinish = getSlotSurfaceOverride(normalizedSurfacePolicy, index);
                        const effectiveFinish = getEffectiveSurfaceFinish({
                            material,
                            materialIndex: index,
                            policy: normalizedSurfacePolicy,
                        });
                        const usesFixedUnpolishedTexture = effectiveFinish === SURFACE_FINISH.UNPOLISHED;
                        const previewUrl = usesFixedUnpolishedTexture
                            ? (unpolishedTextureUrl || originalTextureUrl)
                            : (usesStoneTexture ? activeTextureUrl : originalTextureUrl);
                        const displayTitle = getSurfaceFinishLabel(detectedFinish);
                        const isLockedUnpolished = (
                            detectedFinish === SURFACE_FINISH.UNPOLISHED
                            && !normalizedSurfacePolicy.allowPolishOverride
                        );

                        return (
                            <div
                                className={`adminMaterialVariantSelector__slot ${usesFixedUnpolishedTexture ? 'is-fixed-unpolished' : (usesStoneTexture ? 'is-stone' : 'is-original')} finish-${effectiveFinish}`}
                                key={`${material?.uuid || originalName}-${index}`}
                            >
                                <div
                                    className={`adminMaterialVariantSelector__slotPreview ${previewUrl ? 'has-texture' : ''}`}
                                    style={previewUrl ? { backgroundImage: `url(${previewUrl})` } : undefined}
                                    aria-hidden="true"
                                >
                                    {!previewUrl ? (usesFixedUnpolishedTexture ? '▧' : (usesStoneTexture ? '◆' : '✦')) : null}
                                </div>

                                <div className="adminMaterialVariantSelector__slotBody">
                                    <div className="adminMaterialVariantSelector__slotName">
                                        <div>
                                            <strong>{displayTitle}</strong>
                                            <span title={originalName}>GLB: {originalName}</span>
                                        </div>
                                        <small>Слот #{index + 1}</small>
                                    </div>

                                    <div className="adminMaterialVariantSelector__slotControls">
                                        {usesFixedUnpolishedTexture ? (
                                            <div className="adminMaterialVariantSelector__fixedTexture">
                                                <span>Текстура</span>
                                                <strong>
                                                    <LockOutlinedIcon fontSize="inherit" />
                                                    {unpolishedSurfaceCatalog?.name || 'Неполированная поверхность'}
                                                </strong>
                                                <small>
                                                    Фиксированная текстура для неполированных частей модели.
                                                </small>
                                            </div>
                                        ) : (
                                            <label>
                                                <span>Камень</span>
                                                <select
                                                    value={usesStoneTexture ? 'stone' : 'original'}
                                                    onChange={(event) => setSlotTextureMode(index, event.target.value)}
                                                    disabled={saving || !activeVariant}
                                                    aria-label={`Текстура для ${originalName}`}
                                                >
                                                    <option value="stone">
                                                        {activeCatalog?.name || activeVariant?.materialName || 'Материал изделия'}
                                                    </option>
                                                    <option value="original">Оригинальная текстура модели</option>
                                                </select>
                                            </label>
                                        )}

                                        {normalizedSurfacePolicy.allowPolishOverride ? (
                                            <label>
                                                <span>Обработка</span>
                                                <select
                                                    value={explicitFinish}
                                                    onChange={(event) => setSlotFinish(index, event.target.value)}
                                                    disabled={saving}
                                                    aria-label={`Обработка для ${originalName}`}
                                                >
                                                    <option value={SURFACE_FINISH.AUTO}>
                                                        По модели: {getSurfaceFinishShortLabel(detectedFinish)}
                                                    </option>
                                                    <option value={SURFACE_FINISH.UNPOLISHED}>Неполированная</option>
                                                    <option value={SURFACE_FINISH.POLISHED}>Полированная</option>
                                                </select>
                                            </label>
                                        ) : (
                                            <div className={`adminMaterialVariantSelector__finishBadge ${isLockedUnpolished ? 'is-locked' : ''}`}>
                                                {isLockedUnpolished ? <LockOutlinedIcon fontSize="inherit" /> : null}
                                                {getSurfaceFinishShortLabel(effectiveFinish)}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>

                {hasDetectedUnpolishedSlots && !unpolishedTexturePath ? (
                    <p className="adminMaterialVariantSelector__warning">
                        В справочнике не найдена активная «Неполированная поверхность» с JPG. Неполированные части останутся с исходной текстурой GLB.
                    </p>
                ) : null}

                {!activeTexturePath ? (
                    <p className="adminMaterialVariantSelector__warning">
                        У выбранного камня не задан путь к JPG. Цена работает, но заменить текстуру в 3D нельзя.
                    </p>
                ) : null}
                </div>
            </details>
        </section>
    );
};

export default AdminMaterialVariantSelector;
